import React, { useCallback, useEffect, useState } from 'react';
import { Sparkles, Upload, Trash2, Loader2, Image as ImageIcon, ChevronUp, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { api } from '../../App';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

/** Admin-only: photos that replace the home-page slideshow while a festival theme is on. */
export function FestivalPhotosPanel({ token }) {
  const [data, setData] = useState({ festivals: {}, photos: [], active_theme: 'default' });
  const [festival, setFestival] = useState('navratri');
  const [files, setFiles] = useState([]);
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try { setData(await api.getAuth('/api/admin/festival-photos', token)); } catch (e) { toast.error(e.message); }
    setLoading(false);
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    if (!files.length) { toast.error('Choose one or more photos first'); return; }
    setBusy(true);
    let ok = 0;
    for (const f of files) {
      try {
        const fd = new FormData(); fd.append('file', f);
        const res = await api.fetchAuth('/api/admin/upload?category=festival', token, { method: 'POST', body: fd });
        const j = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(j.detail || `Upload failed for ${f.name}`);
        await api.postAuth('/api/admin/festival-photos', { festival, file_id: j.file_id, url: j.url, title, subtitle }, token);
        ok += 1;
      } catch (e) { toast.error(e.message, { duration: 8000 }); }
    }
    if (ok) toast.success(`${ok} photo${ok > 1 ? 's' : ''} added to the ${data.festivals[festival] || festival} slideshow`);
    setFiles([]); setTitle(''); setSubtitle(''); setBusy(false); load();
  };
  const remove = async (p) => {
    if (!window.confirm('Remove this photo from the festival slideshow? (The file stays in Media & Files.)')) return;
    try { await api.deleteAuth(`/api/admin/festival-photos/${p.photo_id}`, token); toast.success('Photo removed'); load(); } catch (e) { toast.error(e.message); }
  };
  const move = async (list, idx, dir) => {
    const next = [...list]; const j = idx + dir; if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    try { await api.putAuth('/api/admin/festival-photos/reorder', { festival, photo_ids: next.map(p => p.photo_id) }, token); load(); } catch (e) { toast.error(e.message); }
  };

  const mine = data.photos.filter(p => p.festival === festival);
  const isActive = data.active_theme === festival;
  return (
    <Card data-testid="festival-photos-panel"><CardContent className="p-5 space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1">
          <h3 className="font-semibold flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}><Sparkles className="w-4 h-4" /> Festival hero photos</h3>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">While a festival theme is on (chosen above or automatically by date), the home-page slideshow switches to these photos. Remove them all and the normal slideshow returns.</p>
        </div>
        <Select value={festival} onValueChange={setFestival}>
          <SelectTrigger className="w-full sm:w-64" data-testid="festival-photos-select"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(data.festivals).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="text-xs flex flex-wrap items-center gap-2">
        <span className="text-[hsl(var(--muted-foreground))]">Current site theme:</span> <Badge variant="outline" data-testid="festival-photos-active-theme">{data.festivals[data.active_theme] || 'Default (heritage slideshow)'}</Badge>
        {isActive && mine.length > 0 && <Badge className="text-xs" data-testid="festival-photos-live">Showing on the home page now</Badge>}
        {isActive && mine.length === 0 && <span className="text-amber-700">Theme is on but no photos yet — the normal slideshow is showing.</span>}
      </div>

      <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-2 items-end">
        <div><label className="text-xs text-[hsl(var(--muted-foreground))]">Caption (optional)</label><Input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Navratri Garba 2025" data-testid="festival-photos-title" /></div>
        <div><label className="text-xs text-[hsl(var(--muted-foreground))]">Sub-caption (optional)</label><Input value={subtitle} onChange={e => setSubtitle(e.target.value)} placeholder="e.g. Nine nights of dance and devotion" data-testid="festival-photos-subtitle" /></div>
        <div className="flex gap-2">
          <input type="file" accept="image/*,.heic,.heif" multiple onChange={e => setFiles(Array.from(e.target.files || []))} className="text-xs w-44" aria-label="Choose festival photos" data-testid="festival-photos-input" />
          <Button size="sm" disabled={!files.length || busy} onClick={upload} data-testid="festival-photos-upload">{busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />} Add {files.length > 1 ? `${files.length} photos` : 'photo'}</Button>
        </div>
      </div>

      {loading ? <div className="h-28 rounded-xl bg-[hsl(var(--muted))] animate-pulse" /> : mine.length === 0 ? (
        <div className="rounded-xl border border-dashed p-6 text-center text-sm text-[hsl(var(--muted-foreground))]" data-testid="festival-photos-empty"><ImageIcon className="w-6 h-6 mx-auto mb-1" /> No {data.festivals[festival]} photos yet. Landscape photos (16:9) from past celebrations work best.</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="festival-photos-grid">
          {mine.map((p, i) => (
            <div key={p.photo_id} className="rounded-xl border overflow-hidden bg-[hsl(var(--muted))]" data-testid={`festival-photo-${p.photo_id}`}>
              <img src={`${BACKEND}${p.thumb_url}`} alt={p.title || 'Festival photo'} className="w-full h-28 object-cover" loading="lazy" />
              <div className="p-2 text-xs">
                <div className="truncate font-medium">{p.title || <span className="text-[hsl(var(--muted-foreground))]">No caption</span>}</div>
                <div className="flex items-center justify-between mt-1">
                  <div className="flex gap-0.5">
                    <Button size="icon" variant="ghost" className="h-7 w-7" disabled={i === 0} onClick={() => move(mine, i, -1)} aria-label="Move earlier"><ChevronUp className="w-3.5 h-3.5" /></Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7" disabled={i === mine.length - 1} onClick={() => move(mine, i, 1)} aria-label="Move later"><ChevronDown className="w-3.5 h-3.5" /></Button>
                  </div>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-[hsl(var(--destructive))]" onClick={() => remove(p)} aria-label="Remove photo" data-testid={`festival-photo-remove-${p.photo_id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </CardContent></Card>
  );
}
